import React, { useState, useEffect } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Form, Button } from 'semantic-ui-react'
import FWOIteractiveTemplateRow from '../helpers/FWOIteractiveTemplateRow'
import {
  getInitialValues,
  getFieldSchema,
  buildAddressFromValues,
  getPageFields,
} from '../common'
import {
  FormKey,
  IInteractiveTemplateField,
  IInteractiveTemplatePage,
  IInteractiveTemplateSection,
} from '../../../models/InteractiveTemplate'
import Conditional from '../../common/Conditional'
import Accordion from '../../common/Accordion'
import PageTitle from '../../PageTitle'

enum Page {
  EmploymentDetails = 'Employment details',
  RecordDetails = 'Record details',
}

export const RequestForRecordsTemplate = (props) => {
  const [initialValues, setInitialValues] = useState({})
  const [validationSchema, setValidationSchema] = useState(null)
  const [showEmploymentDetailsPage, setShowEmploymentDetailsPage] =
    useState(false)
  const [showRecordDetailsPage, setShowRecordDetailsPage] = useState(false)
  const [currentPage, setCurrentPage] = useState(null)
  const [validEmploymentDetails, setValidEmploymentDetails] = useState(false)
  const [workTimeframe, setWorkTimeframe] = useState('')
  const [collectAtPremise, setCollectAtPremise] = useState('')
  const [requestingRecords, setRequestingRecords] = useState([])

  useEffect(() => {
    const values = getInitialValues(props.template)
    values['RECORD_REQUEST'] = [] // this will triger array validation
    setInitialValues(values)
  }, [])

  useEffect(() => {
    setCurrentPage(props.show ? Page.EmploymentDetails : null)
  }, [props.show])

  useEffect(() => {
    const schema = getPageSchema(currentPage)
    setValidationSchema(schema)
    if (currentPage === Page.EmploymentDetails) {
      setShowEmploymentDetailsPage(true)
      setShowRecordDetailsPage(false)
    } else if (currentPage === Page.RecordDetails) {
      setShowEmploymentDetailsPage(false)
      setShowRecordDetailsPage(true)
    }
  }, [currentPage])

  const getPageSchema = (currentPage) => {
    const page = props.template?.pages?.find(
      (page) => page.name === currentPage
    )

    if (!page) {
      return
    }

    const fields = getPageFields(page)

    const schema = fields.reduce((schema, field) => {
      if (field.replacement === 'AUTHOR_END') {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('WORK_TIMEFRAME', {
            is: (ATTEND_MEETING) =>
              ATTEND_MEETING === 'My whole period of employment',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (
        field.replacement === 'RECORD_START' ||
        field.replacement === 'RECORD_END'
      ) {
        return {
          ...schema,
          [field.replacement]: Yup.string().when('WORK_TIMEFRAME', {
            is: (PREVIOUS_WARNING) => PREVIOUS_WARNING === 'A different period',
            then: Yup.string().required(`${field.label} is required`),
          }),
        }
      }

      if (field.replacement === 'RECORD_REQUEST') {
        return {
          ...schema,
          [field.replacement]: Yup.array().min(1, `${field.label} is required`),
        }
      }

      if (field.replacement === 'OTHER_RECORDS') {
        return schema
        // return {
        //   ...schema,
        //   [field.replacement]: Yup.string().test(
        //     'OTHER_RECORDS', `${field.label} is required`, function(value) {
        //       console.log(this.parent.RECORD_REQUEST)
        //       return (this.parent.RECORD_REQUEST === undefined || this.parent.RECORD_REQUEST.indexOf('Other') === -1) && value === undefined
        //     }
        //   ),
        // }
      }

      if (field.replacement === 'POSTAL_ADDRESS') {
        return {
          ...schema,
          [`${field.replacement}_STREET`]: Yup.string().when(
            'COLLECT_AT_PREMISE',
            {
              is: (COLLECT_AT_PREMISE) => COLLECT_AT_PREMISE === 'No',
              then: Yup.string().required(`${field.label} is required`),
            }
          ),
          [`${field.replacement}_SUBURB`]: Yup.string().when(
            'COLLECT_AT_PREMISE',
            {
              is: (COLLECT_AT_PREMISE) => COLLECT_AT_PREMISE === 'No',
              then: Yup.string().required(`${field.label} is required`),
            }
          ),
          [`${field.replacement}_POSTCODE`]: Yup.string().when(
            'COLLECT_AT_PREMISE',
            {
              is: (COLLECT_AT_PREMISE) => COLLECT_AT_PREMISE === 'No',
              then: Yup.string().required(`${field.label} is required`),
            }
          ),
          [`${field.replacement}_STATE`]: Yup.string().when(
            'COLLECT_AT_PREMISE',
            {
              is: (COLLECT_AT_PREMISE) => COLLECT_AT_PREMISE === 'No',
              then: Yup.string().required(`${field.label} is required`),
            }
          ),
        }
      }

      return getFieldSchema(schema, field)
    }, {})

    return Yup.object().shape(schema)
  }

  const renderField = (field: IInteractiveTemplateField, index: number) => {
    if (field.replacement === 'RECORD_REQUEST') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onChange={(e) => {
            const index = requestingRecords.indexOf(e.target.value)
            if (index !== -1 && !e.target.checked) {
              // remove element
              requestingRecords.splice(index, 1)
              setRequestingRecords(requestingRecords)
              setInitialValues({
                ...initialValues,
                RECORD_REQUEST: requestingRecords,
              })
            } else if (index === -1 && e.target.checked) {
              // add element
              setRequestingRecords([...requestingRecords, e.target.value])
              setInitialValues({
                ...initialValues,
                RECORD_REQUEST: [...requestingRecords, e.target.value],
              })
            }
          }}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'WORK_TIMEFRAME') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setWorkTimeframe(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'AUTHOR_END') {
      if (workTimeframe === 'my whole period of employment') {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
          ></FWOIteractiveTemplateRow>
        )
      }
      return null
    }

    if (
      field.replacement === 'RECORD_START' ||
      field.replacement === 'RECORD_END'
    ) {
      if (workTimeframe === 'a different period') {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
          ></FWOIteractiveTemplateRow>
        )
      }
      return null
    }

    if (field.replacement === 'COLLECT_AT_PREMISE') {
      return (
        <FWOIteractiveTemplateRow
          key={index}
          {...field}
          onSelection={(value) => setCollectAtPremise(value?.toLowerCase())}
        ></FWOIteractiveTemplateRow>
      )
    }

    if (field.replacement === 'POSTAL_ADDRESS') {
      if (collectAtPremise === 'no') {
        return (
          <FWOIteractiveTemplateRow
            key={index}
            {...field}
          ></FWOIteractiveTemplateRow>
        )
      }
      return null
    }

    if (
      field.replacement === 'OTHER_RECORDS' &&
      requestingRecords.find((e) => e === 'Other') === undefined
    ) {
      return null
    }

    return (
      <FWOIteractiveTemplateRow
        key={index}
        {...field}
      ></FWOIteractiveTemplateRow>
    )
  }

  const validatePage = async (validateForm, setState) => {
    return validateForm().then((errors) => {
      console.log(errors)
      const state = Object.keys(errors).length === 0
      setState(state)
      return state
    })
  }

  const renderPage = (
    page: IInteractiveTemplatePage,
    index: number,
    validateForm
  ) => {
    if (page?.name === Page.EmploymentDetails) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showEmploymentDetailsPage}
          onClick={() => {
            if (currentPage !== Page.EmploymentDetails) {
              setCurrentPage(Page.EmploymentDetails)
            }
            setShowEmploymentDetailsPage(!showEmploymentDetailsPage)
          }}
        >
          <Conditional if={showEmploymentDetailsPage}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                className="ui is-primary button form-submit"
                onClick={() =>
                  validatePage(validateForm, setValidEmploymentDetails)
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
    if (page?.name === Page.RecordDetails) {
      return (
        <Accordion
          key={index}
          heading={page.name}
          expanded={showRecordDetailsPage}
          onClick={() => {
            if (!currentPage) {
              return
            }
            validatePage(validateForm, setValidEmploymentDetails).then(
              (isPrevPageValid: boolean) => {
                if (showRecordDetailsPage) {
                  // fold page
                  setShowRecordDetailsPage(false)
                } else {
                  if (isPrevPageValid) {
                    setCurrentPage(Page.RecordDetails)
                    setShowRecordDetailsPage(true)
                    window.scrollTo(0, 0)
                  } else {
                    // keep folded
                    setShowRecordDetailsPage(false)
                  }
                }
              }
            )
          }}
        >
          <Conditional if={validEmploymentDetails}>
            {page.sections?.map((section, index) =>
              renderSection(section, index)
            )}
            <p className="form-submit">
              <Button
                className="ui is-primary button"
                onClick={() =>
                  validateForm().then((errors) => console.log(errors))
                }
              >
                Next
              </Button>
            </p>
          </Conditional>
        </Accordion>
      )
    }
  }

  const renderSection = (
    section: IInteractiveTemplateSection,
    index: number
  ) => {
    if (section?.fields) {
      return (
        <section key={index}>
          <h3>
            {section.name.toLowerCase() !== 'records' ? section.name : null}
          </h3>
          {section.fields.map((field, index) => renderField(field, index))}
        </section>
      )
    } else {
      return <></>
    }
  }

  const onSubmit = (values) => {
    values['BUSINESS_ADDRESS'] = buildAddressFromValues(
      'BUSINESS_ADDRESS',
      values
    )
    setInitialValues({ ...initialValues, ...values })
    if (currentPage === Page.EmploymentDetails) {
      setCurrentPage(Page.RecordDetails)
    } else if (currentPage === Page.RecordDetails) {
      values['POSTAL_ADDRESS'] = buildAddressFromValues(
        'POSTAL_ADDRESS',
        values
      )
      values['SEND_STATEMENT'] = values['SEND_STATEMENT']
        .replace('POSTAL_ADDRESS_STREET', values['POSTAL_ADDRESS_STREET'])
        .replace('POSTAL_ADDRESS_SUBURB', values['POSTAL_ADDRESS_SUBURB'])
        .replace('POSTAL_ADDRESS_STATE', values['POSTAL_ADDRESS_STATE'])
        .replace('POSTAL_ADDRESS_POSTCODE', values['POSTAL_ADDRESS_POSTCODE'])

      if (values['OTHER_RECORDS'] && values['OTHER_RECORDS'].length > 0) {
        values['RECORD_REQUEST'].push(values['OTHER_RECORDS'])
      }

      // remove 'Other' form values
      if (values['RECORD_REQUEST'] && values['RECORD_REQUEST'].length > 0) {
        values['RECORD_REQUEST'] = values['RECORD_REQUEST'].filter(
          (value) => value !== 'Other'
        )
      }

      props.handleNext(values, FormKey.RequestForRecords)
      setShowRecordDetailsPage(false)
    }
  }

  return (
    <>
      <PageTitle title="Request for records - Templates - Fair Work Ombudsman" />
      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        initialValues={initialValues}
        validateOnMount={false}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, validateForm }) => (
          <Form
            className="ui form osp-variant large"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="accordian">
              {props.template?.pages?.map((page, index) =>
                renderPage(page, index, validateForm)
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}
